/**
 * Admin Events List Hook (URQL)
 *
 * Query hook for fetching the list of events in admin.
 */

import { useQuery } from "urql";
import { errorTracking } from "@/lib/error-tracking";
import type { AdminEventListItem } from "../types";

interface AdminEventsResult {
  events: AdminEventListItem[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// =============================================================================
// QUERY
// =============================================================================

const AdminEventsListDocument = `
  query AdminEventsList {
    adminEvents {
      id
      title
      subtitle
      venue
      startsAt
      endsAt
      imageUrl
      status
      visibility
      featured
      isActive
      categories {
        id
        name
        color
      }
      createdAt
    }
  }
`;

// =============================================================================
// HOOK
// =============================================================================

export function useAdminEvents(): AdminEventsResult {
  const [result, reexecute] = useQuery<{
    adminEvents: AdminEventListItem[];
  }>({
    query: AdminEventsListDocument,
  });

  // Track errors
  if (result.error) {
    errorTracking.addBreadcrumb({
      category: "admin.events",
      message: "Failed to load events list",
      level: "error",
    });
  }

  return {
    events: result.data?.adminEvents ?? [],
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}
